import React from 'react';
import { Mono, Th, EmptyState, Callout } from './RmaBits';
import RmaStagePill from './RmaStagePill';
import { RMA_OUTCOMES, RMA_OUTCOME, OPEN_STAGES, stageOf, stageAgeDays, ageTone, TONE } from './rmaHelpers';
import { stripSerial } from '../../utils/scan';

// Screen 4 — book returned units back in.
//
// Two facts are recorded here, and only the first is the manufacturer's: what they did
// (repaired / replaced / no fault found / scrapped), and where the unit goes as a result.
// Everything except a scrapped unit lands in "In Testing" awaiting an optional bench test,
// because "Lightbug says it is fixed" and "it works" are different claims.
//
// The whole session is local state; one setSt when the operator books in.

// Outcome is sized to hold its four toggle buttons and no more — spare width goes to the
// fault text, which is the column that actually benefits from it.
const COLS = '118px 132px minmax(0,1fr) minmax(300px,max-content) 150px';
const OUT_COLS = '112px 96px minmax(0,1fr) 150px 110px';

function RmaReceive({
  st, shipments, devicesById, isReadOnly,
  onBack, onBookIn, onBenchTest,
  focusShipmentId, buzzSuccess, buzzError,
  now = Date.now(),
}) {
  const [scanInput, setScanInput] = React.useState('');
  const [scanError, setScanError] = React.useState('');
  const [rows, setRows] = React.useState([]);   // {deviceId, shipmentId, outcome, newSerial}
  const [held, setHeld] = React.useState([]);   // unknown serials, kept for review
  const [lastResult, setLastResult] = React.useState(null);

  const settings = st.rmaSettings;
  const mfr = settings?.manufacturer || 'the manufacturer';

  // Only open RMAs can receive units, and a unit already given an outcome is done.
  const open = React.useMemo(() => shipments.filter(sh => OPEN_STAGES.includes(stageOf(sh))), [shipments]);
  const awaiting = React.useMemo(() => {
    const out = [];
    open.forEach(sh => {
      if (stageOf(sh) === 'awaiting_dispatch') return;   // never left the bench
      (sh.deviceIds || []).forEach(id => {
        if (!(sh.outcomes || {})[id]) out.push({ shipmentId: sh.id, deviceId: id, shipment: sh });
      });
    });
    return out;
  }, [open]);

  const expectedUnits = awaiting.length;
  const expectedRmas = new Set(awaiting.map(a => a.shipmentId)).size;

  const addSerial = (raw) => {
    const clean = stripSerial(raw);
    if (!clean) return;
    const dev = (st.devices || []).find(d => String(d.serial).toLowerCase() === clean.toLowerCase());
    if (!dev) {
      // Held rather than rejected: an unknown serial coming back in a box is a real event
      // that needs looking at, not an input error to discard.
      setHeld(h => h.some(x => x.serial === clean) ? h : [...h, { serial: clean, reason: 'Serial not in stock' }]);
      setScanError(clean + ' is not a known serial — held for review');
      buzzError && buzzError();
      return;
    }
    if (rows.some(r => r.deviceId === dev.id)) {
      setScanError(clean + ' is already in this session');
      buzzError && buzzError();
      return;
    }
    const match = awaiting.find(a => a.deviceId === dev.id);
    if (!match) {
      setHeld(h => h.some(x => x.serial === clean) ? h : [...h, { serial: clean, reason: 'Not on an open RMA' }]);
      setScanError(clean + ' is not out on an RMA — held for review');
      buzzError && buzzError();
      return;
    }
    setRows(r => [...r, { deviceId: dev.id, shipmentId: match.shipmentId, outcome: 'repaired', newSerial: '' }]);
    setScanError('');
    buzzSuccess && buzzSuccess();
  };

  const setOutcome = (deviceId, outcome) =>
    setRows(rs => rs.map(r => r.deviceId === deviceId ? { ...r, outcome, newSerial: RMA_OUTCOME[outcome]?.needsNewSerial ? r.newSerial : '' } : r));

  const applyToAll = (outcome) =>
    setRows(rs => rs.map(r => ({ ...r, outcome, newSerial: RMA_OUTCOME[outcome]?.needsNewSerial ? r.newSerial : '' })));

  const counts = RMA_OUTCOMES.map(o => ({ ...o, n: rows.filter(r => r.outcome === o.key).length })).filter(o => o.n);
  const needsSerial = rows.filter(r => RMA_OUTCOME[r.outcome]?.needsNewSerial && !r.newSerial.trim());
  const canBook = rows.length > 0 && !isReadOnly && needsSerial.length === 0;

  // Booking in is irreversible from the UI: it writes each unit's outcome, retires the
  // scrapped ones outright and moves the rest onto the bench. Scrapping in particular has
  // no undo, so the confirmation spells out what is about to happen per outcome rather
  // than asking a bare "are you sure?".
  const bookIn = () => {
    if (!canBook) return;
    const pendingBench = rows.filter(r => RMA_OUTCOME[r.outcome]?.needsBenchTest);
    const shipmentIds = [...new Set(rows.map(r => r.shipmentId))];
    const scrapped = rows.filter(r => r.outcome === 'scrapped');
    const lines = [
      'Book in ' + rows.length + ' unit' + (rows.length === 1 ? '' : 's') + '?',
      '',
      ...counts.map(c => '• ' + c.n + ' ' + c.label.toLowerCase() + ' → ' + c.destLabel),
    ];
    if (scrapped.length) {
      lines.push('',
        scrapped.length + ' scrapped unit' + (scrapped.length === 1 ? '' : 's')
        + ' will be retired and written off. This cannot be undone.');
    }
    if (pendingBench.length) {
      lines.push('',
        pendingBench.length + ' unit' + (pendingBench.length === 1 ? '' : 's')
        + ' will await a bench test before returning to stock.');
    }
    if (!window.confirm(lines.join('\n'))) return;
    onBookIn(rows.map(r => ({ ...r, newSerial: r.newSerial.trim() })));
    setLastResult({ booked: rows.length, bench: pendingBench.length, shipmentIds });
    setRows([]);
    setScanError('');
  };

  const fieldStyle = {
    height: 28, padding: '0 8px', borderRadius: 'var(--radius-md)', fontSize: 12,
    background: 'var(--surface-default)', border: '1px solid var(--border-default)',
    color: 'var(--text-default)', outline: 'none', width: '100%', fontFamily: 'var(--font-mono)',
  };
  const outBtn = (on, tone) => ({
    padding: '5px 9px', borderRadius: 'var(--radius-md)', fontSize: 11.5, fontWeight: 600,
    cursor: 'pointer', lineHeight: 1, whiteSpace: 'nowrap',
    border: '1px solid ' + (on ? (TONE[tone]?.border || 'var(--brand-border)') : 'var(--border-default)'),
    background: on ? (TONE[tone]?.fill || 'var(--brand-tint)') : 'var(--surface-default)',
    color: on ? (TONE[tone]?.text || 'var(--brand-light)') : 'var(--text-subtle)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          All RMAs
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>Receive returns</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 4 }}>
              Scan units back in from {mfr}. Each unit gets an outcome and a stock destination.
            </div>
          </div>
          <span className={'pill ' + (expectedUnits ? 'pill-info' : 'pill-neutral')} style={{ flexShrink: 0 }}>
            {expectedUnits} unit{expectedUnits === 1 ? '' : 's'} expected across {expectedRmas} RMA{expectedRmas === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {lastResult && (
        <Callout tone="success" title={'Booked in ' + lastResult.booked + ' unit' + (lastResult.booked === 1 ? '' : 's')}>
          {lastResult.bench > 0
            ? lastResult.bench + ' unit' + (lastResult.bench === 1 ? '' : 's') + ' now awaiting a bench test before returning to stock. '
            : 'Nothing needs a bench test. '}
          {lastResult.bench > 0 && !isReadOnly && (
            <button
              onClick={() => onBenchTest(lastResult.shipmentIds[0])}
              className="btn btn-sm btn-primary"
              style={{ marginTop: 8 }}
            >Bench test now</button>
          )}
        </Callout>
      )}

      {/* Scan. Hidden entirely for a read-only user — booking stock back in is a change. */}
      {isReadOnly ? (
        <Callout tone="info" title="Read only">
          You can see what is outstanding with {mfr}, but booking units back in needs a
          Technician account or above.
        </Callout>
      ) : (
      <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', padding: 18 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface-default)',
          border: '1.5px solid var(--brand-default)', borderRadius: 'var(--radius-lg)',
          padding: '0 14px', height: 46, boxShadow: '0 0 0 3px var(--brand-tint)', maxWidth: 520,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-default)" strokeWidth="2" strokeLinecap="round">
            <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2M7 8v8M11 8v8M15 8v8M18 8v8" />
          </svg>
          <input
            value={scanInput}
            onChange={e => setScanInput(e.target.value)}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const v = scanInput.trim();
              setScanInput('');
              if (v) addSerial(v);
            }}
            placeholder="Scan returned unit…"
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', minWidth: 0 }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-ghost)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>Enter</span>
        </div>
        {scanError && (
          <div style={{ fontSize: 11.5, color: 'var(--danger-text)', marginTop: 8 }}>{scanError}</div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 10 }}>
          The scan matches the serial against open RMAs and fills in the reference. Unknown serials are held for review rather than rejected.
        </div>
        {held.length > 0 && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--warning-fill)', border: '1px solid var(--warning-border)', borderRadius: 'var(--radius-lg)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning-text)', marginBottom: 4 }}>
              {held.length} serial{held.length === 1 ? '' : 's'} held for review
            </div>
            {held.map(h => (
              <div key={h.serial} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--warning-text)', padding: '2px 0' }}>
                <Mono size={11.5} weight={600} color="var(--warning-text)">{h.serial}</Mono>
                <span>— {h.reason}</span>
                <span style={{ flex: 1 }} />
                <button onClick={() => setHeld(x => x.filter(y => y.serial !== h.serial))} className="btn btn-sm btn-ghost">Dismiss</button>
              </div>
            ))}
          </div>
        )}
      </div>

      )}

      {/* Session. Nothing to show a read-only user, who cannot scan anything in. */}
      {!isReadOnly && (
      <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border-default)', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            Received this session ({rows.length})
          </div>
          {rows.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>Apply to all</span>
              {RMA_OUTCOMES.map(o => (
                <button key={o.key} onClick={() => applyToAll(o.key)} style={outBtn(false, o.tone)}>{o.label}</button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-inset)' }}>
          <Th>Serial</Th><Th>RMA</Th><Th>Reported fault</Th><Th>Outcome</Th><Th>Goes to</Th>
        </div>

        {rows.length === 0 && (
          <EmptyState
            title="Nothing scanned yet"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 8v8M11 8v8M15 8v8M18 8v8" /></svg>}
          >
            {expectedUnits > 0
              ? 'Scan the units that came back in the box. ' + expectedUnits + ' are still out with ' + mfr + '.'
              : 'Nothing is currently out with ' + mfr + '.'}
          </EmptyState>
        )}

        {rows.map(r => {
          const d = devicesById.get(r.deviceId);
          const sh = shipments.find(s => s.id === r.shipmentId);
          const def = RMA_OUTCOME[r.outcome];
          const destTone = TONE[def?.tone] || TONE.neutral;
          const fault = sh?.faultMode === 'per_device'
            ? (sh.faultCategories?.[r.deviceId] || '—')
            : (sh?.faultCategory || '—');
          return (
            <div key={r.deviceId} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, padding: '13px 18px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}>
              <Mono>{d?.serial || r.deviceId}</Mono>
              <Mono size={11.5} weight={400} color="var(--info-text)">{sh?.ref || '—'}</Mono>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fault}{sh?.faultDescriptions?.[r.deviceId] ? ' — ' + sh.faultDescriptions[r.deviceId] : ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                {RMA_OUTCOMES.map(o => (
                  <button key={o.key} onClick={() => setOutcome(r.deviceId, o.key)} style={outBtn(o.key === r.outcome, o.tone)}>
                    {o.label}
                  </button>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: destTone.text }}>{def?.destLabel || '—'}</div>
                {def?.needsNewSerial && (
                  <input
                    value={r.newSerial}
                    onChange={e => setRows(rs => rs.map(x => x.deviceId === r.deviceId ? { ...x, newSerial: e.target.value } : x))}
                    placeholder="new serial"
                    style={{ ...fieldStyle, marginTop: 4 }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 18px', background: 'var(--surface-inset)', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-subtle)' }}>
              {rows.length} unit{rows.length === 1 ? '' : 's'} scanned
              {counts.length > 0 && ' · ' + counts.map(c => c.n + ' ' + c.label.toLowerCase()).join(', ')}
              {needsSerial.length > 0 && (
                <span style={{ color: 'var(--danger-text)', fontWeight: 600 }}>
                  {' · ' + needsSerial.length + ' replaced unit' + (needsSerial.length === 1 ? '' : 's') + ' still need a new serial'}
                </span>
              )}
            </div>
            <button onClick={bookIn} disabled={!canBook} className="btn btn-primary" style={{ opacity: canBook ? 1 : 0.45 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              Book in {rows.length} unit{rows.length === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </div>

      )}

      {/* Still outstanding — readable by everyone, including read-only. */}
      <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-default)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
          Still outstanding with {mfr}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: OUT_COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-inset)' }}>
          <Th>RMA</Th><Th>Units</Th><Th>Fault</Th><Th>Stage</Th><Th align="right">In stage</Th>
        </div>
        {(() => {
          const byShipment = new Map();
          awaiting.forEach(a => {
            if (!byShipment.has(a.shipmentId)) byShipment.set(a.shipmentId, { shipment: a.shipment, n: 0 });
            byShipment.get(a.shipmentId).n += 1;
          });
          const list = [...byShipment.values()].sort((a, b) => stageAgeDays(b.shipment, now) - stageAgeDays(a.shipment, now));
          if (!list.length) return <EmptyState title={'Nothing outstanding with ' + mfr} />;
          return list.map(({ shipment: sh, n }) => {
            const total = (sh.deviceIds || []).length;
            const age = stageAgeDays(sh, now);
            const tone = TONE[ageTone(age)];
            return (
              <div key={sh.id} style={{ display: 'grid', gridTemplateColumns: OUT_COLS, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}>
                <Mono>{sh.ref || sh.id}</Mono>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{n} of {total}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sh.faultMode === 'per_device' ? 'Per device' : (sh.faultCategory || '—')}
                </div>
                <div><RmaStagePill shipment={sh} settings={settings} now={now} /></div>
                <div style={{ textAlign: 'right' }}>
                  <Mono size={12.5} color={tone.text === TONE.neutral.text ? 'var(--text-default)' : tone.text}>{age} d</Mono>
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

export default RmaReceive;
